// The sample store: used only while the store service (backend/store, :8797) isn't running. It is built from the
// real starter packages in the repo (store/packages, mounted read-only at /store/packages/), with made-up version
// histories so the dashboard has pending, rejected and yanked versions to show. Pages label it "Sample data".
// Changes (yank, approve, reject) last until the page reloads.

import { ApiError } from "./api.ts";
import { findCreator } from "./catalogue.ts";
import type { StoreBackend } from "./store-client.ts";
import { creatorIdOf, filterAssets, type AssetDetail, type AssetSummary, type AssetVersion, type Check, type Manifest, type ReviewItem } from "./store.ts";

const PACKAGES = "/store/packages/";
export const FAKE_IDS = [
  "threedom/avatar-human",
  "threedom/mannequin",
  "threedom/avatar-hair",
  "threedom/avatar-tshirt",
  "threedom/avatar-jeans",
  "threedom/avatar-sneakers",
  "threedom/cap",
  "threedom/backpack",
  "threedom/crate",
  "threedom/crate-small",
];

const DAY = 86400;
const T0 = Date.UTC(2026, 9, 1) / 1000;

const ALL_PASS: Check[] = [
  { name: "manifest", result: "pass", detail: "asset.json is valid (protocol 0.1)" },
  { name: "hashes", result: "pass", detail: "every file matches its sha256" },
  { name: "code scan", result: "pass", detail: "no fetch, eval, globals or loaders" },
  { name: "render", result: "pass", detail: "renders at default params" },
  { name: "determinism", result: "pass", detail: "two renders are identical" },
  { name: "dispose", result: "pass", detail: "no GPU resources left behind" },
  { name: "budget", result: "pass", detail: "within the declared budget" },
];

interface FakeVersion {
  version: string;
  status: AssetVersion["status"];
  at: number;
  note?: string;
  yankReason?: string;
  checks?: Check[];
  /** edits applied to the package's real asset.json for this version */
  edit?: (m: Manifest) => Manifest;
}

const clone = (m: Manifest): Manifest => structuredClone(m);

/** Made-up histories. Versions without an entry get one listed version: the package's own. */
const HISTORY: Record<string, FakeVersion[]> = {
  "threedom/crate": [
    {
      version: "1.0.0",
      status: "listed",
      at: T0,
      edit: (m) => {
        const x = clone(m);
        delete x.params?.properties?.wear;
        if (x.params?.properties?.planks) x.params.properties.planks.maximum = 5;
        delete x.physics;
        return x;
      },
    },
    { version: "1.1.0", status: "listed", at: T0 + 6 * DAY },
    {
      version: "1.2.0",
      status: "pending",
      at: T0 + 14 * DAY,
      checks: [...ALL_PASS.slice(0, 2), { name: "code scan", result: "warn", detail: "declares the audio capability: plays a creak when hit" }, ...ALL_PASS.slice(3)],
      edit: (m) => {
        const x = clone(m);
        x.params!.properties!.lid = { type: "boolean", default: false, description: "add a hinged lid" };
        x.capabilities = ["audio"];
        x.files = { ...x.files, "dist/three.js": "sha256-Q2hhbmdlZEFkYXB0ZXJDb2RlRm9yVGhlTGlkMTIzNA=", "data/creak.ogg": "sha256-Y3JlYWsub2dnIHNhbXBsZSBoYXNoIGZvciByZXZpZXc=" };
        x.budget = { ...x.budget, triangles: 2600 };
        return x;
      },
    },
  ],
  "threedom/cap": [
    { version: "0.9.0", status: "yanked", at: T0 + 2 * DAY, yankReason: "The brim clipped through long hair. Fixed in 1.0.0." },
    { version: "1.0.0", status: "listed", at: T0 + 5 * DAY },
  ],
  "threedom/backpack": [
    { version: "1.0.0", status: "listed", at: T0 + 3 * DAY },
    {
      version: "1.1.0",
      status: "rejected",
      at: T0 + 12 * DAY,
      note: "The straps float about 2 cm off the shoulders at build 1.3. Fit them to the chest bone and send it again.",
      checks: [...ALL_PASS.slice(0, 3), { name: "render", result: "warn", detail: "straps don't touch the body at build 1.3" }, ...ALL_PASS.slice(4)],
      edit: (m) => {
        const x = clone(m);
        x.title = "Small Backpack (roll-top)";
        x.price = 200;
        return x;
      },
    },
  ],
  "threedom/mannequin": [
    { version: "1.0.0", status: "listed", at: T0 },
    {
      version: "1.0.1",
      status: "pending",
      at: T0 + 15 * DAY,
      edit: (m) => {
        const x = clone(m);
        x.tags = [...(x.tags ?? []), "rig"];
        if (x.params?.properties?.height) x.params.properties.height.maximum = 2.2;
        return x;
      },
    },
  ],
};

interface State {
  manifests: Map<string, Manifest>;
  versions: Map<string, FakeVersion[]>;
  bytes: Map<string, number>;
}

let state: Promise<State> | null = null;

function load(): Promise<State> {
  state ??= (async () => {
    const manifests = new Map<string, Manifest>();
    await Promise.all(
      FAKE_IDS.map(async (id) => {
        const r = await fetch(`${PACKAGES}${id}/asset.json`);
        if (r.ok) manifests.set(id, (await r.json()) as Manifest);
      }),
    );
    const versions = new Map<string, FakeVersion[]>();
    for (const [id, m] of manifests) {
      const h = HISTORY[id]?.map((v) => ({ ...v })) ?? [{ version: m.version, status: "listed" as const, at: T0 + (FAKE_IDS.indexOf(id) % 7) * DAY }];
      versions.set(id, h);
    }
    return { manifests, versions, bytes: new Map() };
  })();
  return state;
}

function manifestAt(s: State, id: string, v: FakeVersion): Manifest {
  const base = s.manifests.get(id)!;
  const m = v.edit ? v.edit(base) : clone(base);
  return { ...m, version: v.version };
}

function latestListed(vs: FakeVersion[]): FakeVersion | undefined {
  return [...vs].reverse().find((v) => v.status === "listed");
}

/** Package size from the files' Content-Length (HEAD on the mount). */
async function sizeOf(s: State, id: string): Promise<number | undefined> {
  if (s.bytes.has(id)) return s.bytes.get(id);
  const m = s.manifests.get(id);
  if (!m?.files) return undefined;
  const sizes = await Promise.all(
    [...Object.keys(m.files), "asset.json"].map(async (f) => {
      const r = await fetch(`${PACKAGES}${id}/${f}`, { method: "HEAD" }).catch(() => null);
      return Number(r?.headers.get("content-length") ?? 0);
    }),
  );
  const total = sizes.reduce((a, b) => a + b, 0);
  s.bytes.set(id, total);
  return total;
}

function summary(s: State, id: string, v: FakeVersion): AssetSummary {
  const m = manifestAt(s, id, v);
  return {
    id,
    title: m.title,
    kind: m.kind,
    tags: m.tags ?? [],
    conventions: m.conventions ?? [],
    creator: { handle: m.author.handle, name: m.author.handle === "threedom" ? "Threedom" : m.author.handle, verified: m.author.verified === true, creatorId: creatorIdOf(m.author.handle) },
    price: m.price ?? 0,
    licence: m.licence.tier,
    cosmetic: m.cosmetic === true,
    version: v.version,
    previewUrl: `${PACKAGES}${id}/preview.png`,
    updatedAt: v.at,
  };
}

async function detail(s: State, id: string, all: boolean): Promise<AssetDetail | null> {
  const vs = s.versions.get(id);
  if (!vs) return null;
  const top = latestListed(vs) ?? (all ? vs[vs.length - 1] : undefined);
  if (!top) return null;
  const bytes = await sizeOf(s, id);
  const versions: AssetVersion[] = vs
    .filter((v) => all || v.status === "listed" || v.status === "yanked")
    .map((v) => ({
      version: v.version,
      status: v.status,
      publishedAt: v.at,
      note: v.note ?? null,
      yankReason: v.yankReason ?? null,
      checks: v.checks ?? ALL_PASS,
      signed: v.status === "listed",
      stats: bytes ? { bytes } : undefined,
      base: `${PACKAGES}${id}/`,
      manifest: manifestAt(s, id, v),
    }))
    .reverse();
  const sum = summary(s, id, top);
  return { ...sum, stats: bytes ? { bytes } : undefined, manifest: manifestAt(s, id, top), base: `${PACKAGES}${id}/`, versions, downloads: 40 + ((id.length * 37) % 400) };
}

export const fakeStore: StoreBackend = {
  live: false,
  async search(f) {
    const s = await load();
    const list: AssetSummary[] = [];
    for (const [id, vs] of s.versions) {
      const top = latestListed(vs);
      if (top) list.push(summary(s, id, top));
    }
    return filterAssets(list, f);
  },
  async asset(id) {
    return detail(await load(), id, false);
  },
  async creator(handle) {
    const s = await load();
    const assets = await fakeStore.search({ sort: "title" });
    const mine = assets.filter((a) => a.creator.handle === handle);
    if (!mine.length && ![...s.manifests.values()].some((m) => m.author.handle === handle)) {
      // same as the live store: a handle-only creator (accounts) has a page with no assets
      const found = await findCreator(creatorIdOf(handle)).catch(() => null);
      return found ? { handle, name: found.creator.name, creatorId: found.creator.id, assets: [] } : null;
    }
    return { handle, name: handle === "threedom" ? "Threedom" : handle, creatorId: creatorIdOf(handle), assets: mine };
  },
  async mine() {
    // sample data: every signed-in user sees the starter creator's assets as their own
    const s = await load();
    const out: AssetDetail[] = [];
    for (const id of s.versions.keys()) {
      const d = await detail(s, id, true);
      if (d) out.push(d);
    }
    return out;
  },
  async yank(id, version, reason) {
    const s = await load();
    const v = s.versions.get(id)?.find((x) => x.version === version);
    if (!v) throw new ApiError(404, "version_not_found", "No such version.");
    if (v.status !== "listed") throw new ApiError(409, "not_listed", "Only listed versions can be yanked.");
    v.status = "yanked";
    v.yankReason = reason || undefined;
  },
  async queue() {
    const s = await load();
    const out: ReviewItem[] = [];
    for (const [id, vs] of s.versions) {
      vs.forEach((v, i) => {
        if (v.status !== "pending") return;
        const m = manifestAt(s, id, v);
        const prev = [...vs.slice(0, i)].reverse().find((p) => p.status === "listed" || p.status === "yanked");
        out.push({
          id,
          version: v.version,
          title: m.title,
          kind: m.kind,
          creator: { handle: m.author.handle, name: m.author.handle === "threedom" ? "Threedom" : undefined },
          submittedAt: v.at,
          checks: v.checks ?? ALL_PASS,
          manifest: m,
          previous: prev ? { version: prev.version, manifest: manifestAt(s, id, prev) } : null,
          base: `${PACKAGES}${id}/`,
          previewUrl: `${PACKAGES}${id}/preview.png`,
          hasCode: !!m.engines && Object.keys(m.engines).length > 0,
          renderMissing: false,
        });
      });
    }
    return out.sort((a, b) => a.submittedAt - b.submittedAt);
  },
  async review(id, version, decision, note) {
    const s = await load();
    const v = s.versions.get(id)?.find((x) => x.version === version);
    if (!v || v.status !== "pending") throw new ApiError(409, "not_pending", "That version isn't waiting for review any more.");
    if (decision === "reject" && !note.trim()) throw new ApiError(400, "note_required", "Say what needs fixing.");
    v.status = decision === "approve" ? "listed" : "rejected";
    v.note = note.trim() || undefined;
  },
  openFiles() {
    return true;
  },
  async fileUrl(id, _version, path) {
    return `${PACKAGES}${id}/${path}`;
  },
  async licensed() {
    return null; // the page asks money's inventory
  },
  async runtime() {
    return null; // the install box then shows the runtime's URL pattern
  },
  async renderQueue() {
    return { counts: { queued: 0, running: 0, failed: 0, done: 0 }, jobs: new Map() };
  },
  async retryRender() {
    throw new ApiError(409, "sample_store", "The sample store has no render reviewer.");
  },
};
